import { useState } from "react";
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
} from "@datapay-by-datacom/design-system";
import { useAuthenticated, useIsProvisioner } from "../../../authn";
import {
  formatMarkdownRetrieveConfigSummary,
  formatVectorKbConfigSummary,
  getCapabilityBindingLabel,
  isValidMarkdownRetrieveInputSchema,
  isValidVectorKbInputSchema,
  MARKDOWN_RETRIEVE_BINDING,
  MARKDOWN_RETRIEVE_DOC_ID_PROPERTY,
  parseMarkdownRetrieveConfig,
  listVectorKbToolParameterNames,
  parseVectorKbConfig,
  parseVectorKbToolParameters,
  RETRIEVAL_VECTOR_KB_BINDING,
  useAgents,
  useCapabilities,
} from "../../../agent-catalog";
import type {
  Agent,
  AgentServiceAccount,
  Capability,
  OrgCategory,
  UserOrgRole,
  UserOrgRoleRole,
} from "../../../generated/core-api";
import { useWorkspace } from "../../../workspace";
import { useOrgCtx, useOrgParams, usePermissions } from "../../hooks";
import {
  useCreateServiceAccount,
  useDeleteServiceAccount,
  useGrantCapability,
  useRevokeCapability,
  useServiceAccountsForAgents,
  useSyncAllCapabilities,
  useSyncCapability,
} from "../../hooks/OrgAgent/useOrgAgentServiceAccounts";
import { filterServiceAccountsForOrg } from "../../utils/filterServiceAccountsForOrg";
import {
  extraOrgRoleLabel,
  getServiceAccountLabel,
} from "../../utils/serviceAccountDisplay";
import {
  assignableOrgRoles,
  maxAssignableRole,
} from "../../utils/orgRoleAssignment";
import { CreateAccordion } from "./CreateAccordion";
import styles from "./OrgAgent.module.css";

type ExtraOrgRole = {
  org_id: string;
  org_category: OrgCategory;
  role: UserOrgRoleRole;
};

function formatGrantDate(value: string | null | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  return new Date(value).toLocaleString();
}

function MarkdownRetrieveSnapshotDetails({
  snapshot,
  bindingLabel,
}: {
  snapshot: Capability;
  bindingLabel: string;
}) {
  const config = parseMarkdownRetrieveConfig(snapshot.config);
  const schemaIsValid = isValidMarkdownRetrieveInputSchema(
    snapshot.input_schema,
  );

  return (
    <div className={styles.grantSnapshot}>
      <div className={styles.metaRow}>
        <span className={styles.badge}>{bindingLabel}</span>
        {schemaIsValid && (
          <span className={styles.badge}>
            {MARKDOWN_RETRIEVE_DOC_ID_PROPERTY}
          </span>
        )}
      </div>
      {!schemaIsValid && (
        <p className={styles.errorText}>
          Snapshot input_schema does not match the markdown.retrieve contract.
          Sync from catalog after updating the capability.
        </p>
      )}
      <p className={styles.muted}>
        {formatMarkdownRetrieveConfigSummary(config)}
      </p>
    </div>
  );
}

function GrantSnapshotDetails({ snapshot }: { snapshot: Capability }) {
  const bindingLabel = getCapabilityBindingLabel(snapshot.binding);
  if (snapshot.binding === MARKDOWN_RETRIEVE_BINDING) {
    return (
      <MarkdownRetrieveSnapshotDetails
        snapshot={snapshot}
        bindingLabel={bindingLabel}
      />
    );
  }
  if (
    snapshot.binding &&
    snapshot.binding !== RETRIEVAL_VECTOR_KB_BINDING
  ) {
    return <p className={styles.muted}>{bindingLabel}</p>;
  }

  const config = parseVectorKbConfig(snapshot.config);
  const parameters = parseVectorKbToolParameters(snapshot.input_schema);
  const schemaIsValid = isValidVectorKbInputSchema(snapshot.input_schema);

  return (
    <div className={styles.grantSnapshot}>
      <div className={styles.metaRow}>
        <span className={styles.badge}>{bindingLabel}</span>
        {schemaIsValid &&
          listVectorKbToolParameterNames(parameters).map((parameter) => (
            <span key={parameter} className={styles.badge}>
              {parameter}
            </span>
          ))}
      </div>
      {!schemaIsValid && (
        <p className={styles.errorText}>
          Snapshot input_schema does not match the fused retrieval.vector_kb
          contract. Sync from catalog after updating the capability.
        </p>
      )}
      <p className={styles.muted}>{formatVectorKbConfigSummary(config)}</p>
    </div>
  );
}

function CapabilityGrants({
  agentId,
  serviceAccount,
  catalogCapabilities,
}: {
  agentId: string;
  serviceAccount: AgentServiceAccount;
  catalogCapabilities: { id: string; name: string }[];
}) {
  const syncOne = useSyncCapability(agentId, serviceAccount.id);
  const syncAll = useSyncAllCapabilities(agentId, serviceAccount.id);
  const revoke = useRevokeCapability(agentId, serviceAccount.id);
  const grant = useGrantCapability(agentId, serviceAccount.id);

  const grants = serviceAccount.capability_grants ?? [];
  const grantedIds = new Set(grants.map((g) => g.source_capability_id));
  const ungranted = catalogCapabilities.filter((cap) => !grantedIds.has(cap.id));
  const grantSelectId = `grant-capability-${serviceAccount.id}`;

  return (
    <div className={styles.grantsSection}>
      <div className={styles.grantsHeader}>
        <h4>Capability grants</h4>
        <button
          type="button"
          className={styles.syncButton}
          title="Sync all capability snapshots"
          disabled={syncAll.isPending || grants.length === 0}
          onClick={() => syncAll.mutate()}
        >
          {syncAll.isPending ? "Syncing all…" : "↻ Sync all"}
        </button>
      </div>
      {grants.length === 0 ? (
        <p className={styles.muted}>No capabilities granted yet.</p>
      ) : (
        <ul className={styles.grantList}>
          {grants.map((grantRow) => {
            const grantedAt = formatGrantDate(grantRow.granted_at);
            const syncedAt = formatGrantDate(grantRow.synced_at);
            return (
              <li
                key={grantRow.source_capability_id}
                className={styles.grantRow}
              >
                <div>
                  <strong>{grantRow.snapshot.name}</strong>
                  <GrantSnapshotDetails snapshot={grantRow.snapshot} />
                  <div className={styles.muted}>
                    {grantedAt ? `Granted ${grantedAt}` : "Granted"}
                    {syncedAt ? ` · Synced ${syncedAt}` : " · Not synced"}
                  </div>
                </div>
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.syncButton}
                    title="Sync snapshot from catalog"
                    disabled={syncOne.isPending}
                    onClick={() =>
                      syncOne.mutate(grantRow.source_capability_id)
                    }
                  >
                    ↻
                  </button>
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={() =>
                      revoke.mutate(grantRow.source_capability_id)
                    }
                    isDisabled={revoke.isPending}
                  >
                    Revoke
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {ungranted.length > 0 && (
        <div className={styles.grantNew}>
          <label htmlFor={grantSelectId}>Grant capability</label>
          <select
            id={grantSelectId}
            className={styles.select}
            defaultValue=""
            onChange={(e) => {
              const capabilityId = e.target.value;
              if (capabilityId) {
                grant.mutate(capabilityId);
                e.target.value = "";
              }
            }}
          >
            <option value="">Select capability…</option>
            {ungranted.map((cap) => (
              <option key={cap.id} value={cap.id}>
                {cap.name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

function ServiceAccountCard({
  account,
  agent,
  catalogCapabilities,
  orgRole,
  extraRoleLabels,
}: {
  account: AgentServiceAccount;
  agent: Agent | undefined;
  catalogCapabilities: { id: string; name: string }[];
  orgRole: string | undefined;
  extraRoleLabels: string[];
}) {
  const [isManaging, setIsManaging] = useState(false);
  const deleteMutation = useDeleteServiceAccount(account.agent_id);
  const grants = account.capability_grants ?? [];

  return (
    <div
      className={`${styles.cardCell} ${isManaging ? styles.cardCellEditing : ""}`}
    >
      <Card>
        <CardHeader>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>
              {getServiceAccountLabel(account)}
            </h3>
          </div>
        </CardHeader>
        <CardBody>
          <div className={styles.metaRow}>
            {agent && (
              <span className={styles.badge}>{agent.blueprint.name}</span>
            )}
            {orgRole && <span className={styles.badge}>{orgRole}</span>}
            {extraRoleLabels.map((label) => (
              <span key={label} className={styles.badge}>
                {label}
              </span>
            ))}
            {!isManaging &&
              grants.map((grantRow) => (
                <span
                  key={grantRow.source_capability_id}
                  className={styles.badge}
                >
                  {grantRow.snapshot.name}
                </span>
              ))}
          </div>
          {isManaging && (
            <CapabilityGrants
              agentId={account.agent_id}
              serviceAccount={account}
              catalogCapabilities={catalogCapabilities}
            />
          )}
        </CardBody>
        <CardFooter>
          <div className={styles.actions}>
            <Button
              variant="secondary"
              size="small"
              onPress={() => setIsManaging((open) => !open)}
            >
              {isManaging ? "Done" : "Manage"}
            </Button>
            <Button
              variant="secondary"
              size="small"
              onPress={() => deleteMutation.mutate(account.id)}
              isDisabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}

export function OrgServiceAccountSection() {
  const isProvisioner = useIsProvisioner();

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Service accounts</h2>
      {isProvisioner ? (
        <OrgServiceAccountProvisionerContent />
      ) : (
        <p className={styles.muted}>
          Service account configuration requires provisioner access. Contact a
          provisioner to set up agents for this organisation. You can still test
          agents linked here from the panel below when service accounts exist.
        </p>
      )}
    </section>
  );
}

function OrgServiceAccountProvisionerContent() {
  const auth = useAuthenticated();
  const userId = auth.state === "authenticated" ? auth.userId : "";
  const { category, id: orgId } = useOrgParams();
  const { ownRoleInOrg, orgs } = useOrgCtx();
  const { isProvisioner: isProvisionerPerm } = usePermissions();
  const { workspace_id } = useWorkspace();

  const agentsQuery = useAgents();
  const capabilitiesQuery = useCapabilities();
  const agents = agentsQuery.data ?? [];
  const agentIds = agents.map((agent) => agent.id);
  const serviceAccountQueries = useServiceAccountsForAgents(agentIds, true);

  const [createOpen, setCreateOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const createMutation = useCreateServiceAccount(selectedAgentId);

  const [description, setDescription] = useState("");
  const [extraRoles, setExtraRoles] = useState<ExtraOrgRole[]>([]);

  const userRoleInOrg = ownRoleInOrg(orgId)?.role;
  const defaultRole = maxAssignableRole(userRoleInOrg, isProvisionerPerm());

  const orgServiceAccounts = serviceAccountQueries.flatMap((query, index) => {
    const agent = agents[index];
    if (!query.data || !agent) {
      return [];
    }
    return filterServiceAccountsForOrg(query.data, orgId, category).map(
      (account) => ({ account, agent }),
    );
  });

  const selectableOrgs = orgs.filter(
    (org) => org.id !== orgId || org.category !== category,
  );

  const catalogCapabilities = capabilitiesQuery.data ?? [];
  const isLoading =
    agentsQuery.isLoading ||
    serviceAccountQueries.some((query) => query.isLoading);
  const isError =
    agentsQuery.isError ||
    serviceAccountQueries.some((query) => query.isError);

  const buildPrimaryOrgRole = (): UserOrgRole => ({
    user_id: userId,
    org_id: orgId,
    org_category: category,
    role: defaultRole,
  });

  const buildExtraOrgRoles = (): UserOrgRole[] =>
    extraRoles.map((row) => ({
      user_id: userId,
      org_id: row.org_id,
      org_category: row.org_category,
      role: row.role,
    }));

  const resetCreateForm = () => {
    setDescription("");
    setExtraRoles([]);
    setSelectedAgentId("");
    setCreateOpen(false);
  };

  const handleCreate = () => {
    if (!selectedAgentId) {
      return;
    }
    createMutation.mutate(
      {
        workspace_id,
        description: description.trim() || undefined,
        org_roles: [buildPrimaryOrgRole(), ...buildExtraOrgRoles()],
      },
      {
        onSuccess: resetCreateForm,
      },
    );
  };

  const addExtraRole = () => {
    const firstOrg = selectableOrgs[0];
    if (!firstOrg) {
      return;
    }
    const roleInTarget = ownRoleInOrg(firstOrg.id)?.role;
    setExtraRoles([
      ...extraRoles,
      {
        org_id: firstOrg.id,
        org_category: firstOrg.category,
        role: maxAssignableRole(roleInTarget, isProvisionerPerm()),
      },
    ]);
  };

  return (
    <>
      <p className={styles.sectionDescription}>
        Link a platform agent to this workspace with org roles that scope fused
        hybrid retrieval and invocation for this knowledge base.
      </p>

      <CreateAccordion
        title="New service account"
        isOpen={createOpen}
        onToggle={() => setCreateOpen((open) => !open)}
      >
        <div className={styles.fieldRow}>
          <label htmlFor="sa-agent">Agent</label>
          <select
            id="sa-agent"
            className={styles.select}
            value={selectedAgentId}
            onChange={(e) => setSelectedAgentId(e.target.value)}
          >
            <option value="">Select agent…</option>
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.blueprint.name}
              </option>
            ))}
          </select>
        </div>
        <p className={styles.muted}>
          Creates a service account for this organisation as {defaultRole}.
        </p>
        <div className={styles.fieldRow}>
          <label htmlFor="sa-desc">Description (optional)</label>
          <input
            id="sa-desc"
            className={styles.textInput}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className={styles.extraRoles}>
          <h4>Additional org roles</h4>
          {extraRoles.map((row, index) => {
            const roleInTarget = ownRoleInOrg(row.org_id)?.role;
            const roles = assignableOrgRoles(
              roleInTarget,
              isProvisionerPerm(),
            );
            return (
              <div key={index} className={styles.extraRoleRow}>
                <select
                  className={styles.select}
                  value={`${row.org_category}:${row.org_id}`}
                  onChange={(e) => {
                    const [orgCategory, orgIdValue] = e.target.value.split(":");
                    const org = orgs.find(
                      (o) =>
                        o.id === orgIdValue && o.category === orgCategory,
                    );
                    if (!org) {
                      return;
                    }
                    const nextRole = maxAssignableRole(
                      ownRoleInOrg(org.id)?.role,
                      isProvisionerPerm(),
                    );
                    setExtraRoles(
                      extraRoles.map((r, i) =>
                        i === index
                          ? {
                              org_id: org.id,
                              org_category: org.category,
                              role: nextRole,
                            }
                          : r,
                      ),
                    );
                  }}
                >
                  {selectableOrgs.map((org) => (
                    <option
                      key={`${org.category}:${org.id}`}
                      value={`${org.category}:${org.id}`}
                    >
                      {org.name} ({org.category})
                    </option>
                  ))}
                </select>
                <select
                  className={styles.select}
                  value={row.role}
                  onChange={(e) =>
                    setExtraRoles(
                      extraRoles.map((r, i) =>
                        i === index
                          ? {
                              ...r,
                              role: e.target.value as UserOrgRoleRole,
                            }
                          : r,
                      ),
                    )
                  }
                >
                  {roles.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
                <Button
                  variant="secondary"
                  size="small"
                  onPress={() =>
                    setExtraRoles(extraRoles.filter((_, i) => i !== index))
                  }
                >
                  Remove
                </Button>
              </div>
            );
          })}
          <Button
            variant="secondary"
            size="small"
            onPress={addExtraRole}
            isDisabled={selectableOrgs.length === 0}
          >
            Add org role
          </Button>
        </div>

        <ButtonGroup>
          <Button
            onPress={handleCreate}
            isDisabled={
              createMutation.isPending || !userId || !selectedAgentId
            }
          >
            {createMutation.isPending
              ? "Creating…"
              : "Create service account"}
          </Button>
        </ButtonGroup>
        {createMutation.isError && (
          <p className={styles.errorText}>Failed to create service account.</p>
        )}
      </CreateAccordion>

      {isLoading && <p>Loading service accounts…</p>}
      {isError && (
        <p className={styles.errorText}>Failed to load service accounts.</p>
      )}
      {!isLoading && !isError && orgServiceAccounts.length === 0 && (
        <p className={styles.emptyState}>
          No service accounts yet. Open New service account to link an agent.
        </p>
      )}
      {orgServiceAccounts.length > 0 && (
        <div className={styles.cardGrid}>
          {orgServiceAccounts.map(({ account, agent }) => {
            const extraRoleLabels = (account.org_roles ?? [])
              .filter(
                (role) =>
                  role.org_id !== orgId || role.org_category !== category,
              )
              .map((role) => extraOrgRoleLabel(role, orgs));
            const thisOrgRole = account.org_roles?.find(
              (role) =>
                role.org_id === orgId && role.org_category === category,
            );
            return (
              <ServiceAccountCard
                key={account.id}
                account={account}
                agent={agent}
                catalogCapabilities={catalogCapabilities}
                orgRole={thisOrgRole?.role}
                extraRoleLabels={extraRoleLabels}
              />
            );
          })}
        </div>
      )}
    </>
  );
}
